import React, { useState } from 'react';
import { Form, Row, Col, Alert } from 'react-bootstrap';
import { FormattedMessage, injectIntl } from 'react-intl';
import { getSortsummaryData, getTransportsummaryData, ISortSummaryData, ISortSummaryDataCategory, ITransportSummaryData, ITransportSummaryDataCategory } from '../../api/SortsummaryData';
import SorterList from '../../components/SorterList';
import DateSelector from '../../components/DateSelector';
import { WaitModal } from '../../components/ReportModals';
import { getPreviousMonth, IMonthValue } from '../../util/DateUtils';
import SorterSummaryReport from './SorterSummaryReport';
import TransportSummaryReport, { ITransportSummaryReportRow } from './TransportSummaryReport';
import ReportTypeList from '../../components/ReportTypeList';

interface IBrowseSortSummaryReportProps {
  intl: any;
}

const BrowseSortSummaryReport = (props: IBrowseSortSummaryReportProps) => {
  const [,setState] = useState({}); // just to force re-render when upload status changes
  const [startDate, setStartDate] = useState(getPreviousMonth());
  const [endDate, setEndDate] = useState(getPreviousMonth());
  const [sorter, setSorter] = useState('');
  const [sorterName, setSorterName] = useState('');
  const [reportType, setReportType] = useState('sorter');
  const [types, setTypes] = useState([] as {label: string, value: string}[]);
  const [modalWaitOpen, setWaitModalOpen] = useState(false);
  const [transportDataTable, setTransportData] = useState([] as ITransportSummaryReportRow[]);
  const [sorterDataTable, setSorterData] = useState({} as any);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertText, setAlertText] = useState('');

  const handleRadioChange = (e: any) => {
    setReportType(e.target.value);
    clearData();
  }

  const clearData = () => {
    setStartDate(getPreviousMonth());
    setEndDate(getPreviousMonth());
    setWaitModalOpen(false);
    setSorter('');
    setDataLoaded(false);
    setTypes([]);
    setTransportData([]);
    setSorterData([]);
    setShowAlert(false);
    setAlertText('');
  }

  const clearTable = () => {
    setWaitModalOpen(false);
    setDataLoaded(false);
    setTransportData([]);
    setSorterData([]);
    setShowAlert(false);
    setAlertText('');
  }

  const buildTransportRow = (name: string, row: ITransportSummaryDataCategory) => {
    return {
      name,
      km: parseFloat(row.Km),
      eur: parseFloat(row.EUR),
      eurPct: parseFloat(row['%EUR'])
    };
  }

  const buildTransportSummaryTable = (data: ITransportSummaryData) => {
    const dataTable = [];
    dataTable.push(buildTransportRow('COLD', data.COLD));
    dataTable.push(buildTransportRow('CE', data.CE));
    dataTable.push(buildTransportRow('CRT', data.CRT));
    dataTable.push(buildTransportRow('LDA', data.LDA));
    dataTable.push(buildTransportRow('SDA', data.SDA));
    return dataTable;
  }

  const buildSorterSummaryRow = (name: string, type: string, row: ISortSummaryDataCategory) => {
    const description = props.intl.formatMessage({id:`sortsummaryreports.browse.datatable.${type}`});
    const longName = props.intl.formatMessage({id:`sortsummaryreports.browse.datatable.${name}`});
    return {
      name: longName,
      codes: row.SORT_CODES,
      description,
      kg: row.KG,
      eur: row.EUR,
      eurKg: row['EUR/KG']
    }
  }

  const buildSorterSummaryTable = (data: ISortSummaryData) => {
    const dataTable = [];
    dataTable.push(buildSorterSummaryRow('WEEE1', 'cold', data.WEEE1.COLD));
    dataTable.push(buildSorterSummaryRow('WEEE1', 'lda', data.WEEE1.LDA));
    dataTable.push(buildSorterSummaryRow('WEEE1', 'scrap', data.WEEE1.SCRAP));
    dataTable.push(buildSorterSummaryRow('WEEE1', 'solar', data.WEEE1.SOLARPANELS));
    dataTable.push(buildSorterSummaryRow('WEEE2', 'sda', data.WEEE2.SDA));
    dataTable.push(buildSorterSummaryRow('WEEE2', 'scrap', data.WEEE2.SCRAP));
    dataTable.push(buildSorterSummaryRow('WEEE3', 'ict', data.WEEE3.ICT));
    dataTable.push(buildSorterSummaryRow('WEEE3', 'monitors', data.WEEE3.MONITORS));
    dataTable.push(buildSorterSummaryRow('WEEE3', 'scrap', data.WEEE3.SCRAP));
    dataTable.push(buildSorterSummaryRow('WEEE4', 'ce', data.WEEE4.CE));
    dataTable.push(buildSorterSummaryRow('WEEE4', 'crt', data.WEEE4.CRT));
    dataTable.push(buildSorterSummaryRow('WEEE4', 'music', data.WEEE4.MUSIC));
    dataTable.push(buildSorterSummaryRow('WEEE5', 'lamps', data.WEEE5.LAMPS));
    dataTable.push(buildSorterSummaryRow('WEEE6', 'elTools', data.WEEE6.EL_TOOLS));
    dataTable.push(buildSorterSummaryRow('WEEE7', 'toys', data.WEEE7.TOYS));
    dataTable.push(buildSorterSummaryRow('WEEE8', 'health', data.WEEE8.HEALTH));
    dataTable.push(buildSorterSummaryRow('WEEE9', 'firealarms', data.WEEE9.FIRE_ALARMS));
    dataTable.push(buildSorterSummaryRow('WEEE9', 'monitoring', data.WEEE9.MONITORING_TOOLS));
    return dataTable;
  }

  const fetchData = () => {
    setWaitModalOpen(true);
    const typeNames = types.map((t) => t.value);
    if (reportType === 'sorter') {
      getSortsummaryData(sorter, typeNames, startDate, endDate).then((data: any) => {
        if (!data || data.error) {
          let text = "";
          if(data.error.errorID) {
            text += props.intl.formatMessage({id: `summaryreport.error.${data.error.errorID}`});
          }
          if(data.error.value) {
            text += data.error.value;
          }
          if(text==="") {
            text = props.intl.formatMessage({id: 'summaryreport.noreportsfound'});
          }
          setAlertText(text);
          setShowAlert(true);
          return;
        }
        setSorterData(buildSorterSummaryTable(data));
        setDataLoaded(true);
      }).finally(() => {
        setState({});
        setWaitModalOpen(false);
      });
    } else {
      getTransportsummaryData(startDate, endDate).then((d: any) => {
        setTransportData(buildTransportSummaryTable(d));
        setDataLoaded(true);
      }).finally(() => {
        setState({});
        setWaitModalOpen(false);
      });

    }
  }

  const isGetDisabled = () => {
    return (reportType === 'sorter' && (sorterName === props.intl.formatMessage({id:'sorter-list.choose'}) || (!types || types.length === 0)));
  }

  return (
    <div>
      <WaitModal
        messageId="sortsummaryreports.browse.getting"
        modalOpen={modalWaitOpen}
        setModalOpen={(open: boolean) => setWaitModalOpen(open)}
      />
      <h1>
        <FormattedMessage id="sortsummaryreports.cost.header"/>
        -
        <FormattedMessage id="sortsummaryreports.browse.header"/>
      </h1>

      <div className="search-wrap">
        <Row>
        <Col md="12">
          <Form.Group>
          <label><FormattedMessage id="sortsummaryreports.browse.monthrange"/></label>
          <div className="d-flex flex-row row">
            <div className="col-md-6 datepicker-input">
              <DateSelector startPeriod={startDate} endPeriod={endDate} 
                            onChange={(start: IMonthValue, end: IMonthValue) => { setStartDate(start); setEndDate(end); clearTable(); } }
                            dateFormat={'yyyy-MM'} isStartAndEnd={true} />
              </div>
          </div>
          </Form.Group>
        </Col>
          <Col md="12" className="radio-set">

              <label><FormattedMessage id="sortsummaryreports.browse.reporttype"/></label>
              <div className="radio-value">
                <Form.Group>
                  <input
                    type="radio"
                    name="report-type"
                    value="sorter"
                    id="sorter"
                    checked={reportType === 'sorter'}
                    onChange={handleRadioChange}
                  />
                  <label htmlFor="sorter" className={reportType === 'sorter' ? 'label' : 'disabled label'}>
                    <FormattedMessage id="sortsummaryreports.import.sorter"/>
                  </label>
                </Form.Group>
                <Form.Group>
                  <select className="sorter-select form-control"
                          id="sorter-select"
                          value={sorter}
                          onChange={(e) => {
                            setSorter(e.target.value);
                            setSorterName(e.target.options[e.target.selectedIndex].text);
                            setState({});
                            clearTable();
                          }}
                          disabled={reportType === 'expenses'}>
                    { SorterList(props.intl) }
                  </select>
                </Form.Group>
                <Form.Group>
                  <ReportTypeList sorter={sorter} multiselect={true} setType={(selected: any) => setTypes(selected)}/>
                </Form.Group>
              </div>

              <div className="radio-value">
                <Form.Group>
                  <input
                    type="radio"
                    name="report-type"
                    value="expenses"
                    checked={reportType === 'expenses'}
                    onChange={handleRadioChange}
                    id="expenses"
                  />
                  <label htmlFor="expenses" className={reportType === 'expenses' ? 'label' : 'disabled label'}>
                    <FormattedMessage id="sortsummaryreports.import.expenses"/>
                  </label>
                </Form.Group>
              </div>

          </Col>

          <Col md="12" className="form-buttons d-flex flex-row">
          <button
              className="clear-button btn btn-secondary ml-auto"
              onClick={() => {
                clearData();
              }}
              >
              <FormattedMessage id="handlerreports.browse.clear"/>
            </button>
            <button
              className="send-button btn btn-primary"
              disabled={isGetDisabled()}
              onClick={() => {
                fetchData();
              }}
              >
              <FormattedMessage id="handlerreports.browse.get"/>
            </button>
          </Col>
        </Row>
      </div>
        { showAlert &&
          <div className="alert-wrap">
            <Alert variant="warning" onClose={() => setShowAlert(false)} dismissible={true}>
              { alertText }
            </Alert>
          </div>
        }
        <div className="search-results">

          {transportDataTable && Array.isArray(transportDataTable) && dataLoaded && reportType === 'expenses' &&
          <TransportSummaryReport
            startDate={startDate}
            endDate={endDate}
            data={transportDataTable}
          />
          }
          {sorterDataTable && Array.isArray(sorterDataTable) && dataLoaded && reportType === 'sorter' &&
          <SorterSummaryReport
            startDate={startDate}
            endDate={endDate}
            data={sorterDataTable}
            sorter={sorterName}
          />
          }
        </div>

    </div>
  );
}

export default injectIntl(BrowseSortSummaryReport);
